// Formatting helpers local to the reports + search feature (plan.md G25 — S0
// ownership touches only apps/web/src/features/{reports,search}/ and their route
// files, so this deliberately does NOT add to a shared lib/format.ts, which
// doesn't exist yet — every prior web goal (stores-transfers/format.ts,
// silo-counts/utils.ts) duplicated the same small formatters locally for the
// same reason; see those files' headers).
//
// Money is always integer ZAR cents (CLAUDE.md invariant); mass is always kg,
// shown in kg for line-level detail and with a dimmed "(~X t)" annotation for
// the larger site x period aggregates (mass balance, silo drift) per BUILD_SPEC
// §11 ("masses in kg->t where apt").

/** ZAR cents (bigint, its wire decimal-string form, or a plain number) -> "R1,234.56". */
export function formatMoneyCents(cents: bigint | string | number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  const negative = n < 0;
  const rands = Math.abs(n) / 100;
  const formatted = rands.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${negative ? '-' : ''}R${formatted}`;
}

/** A per-unit COST RATE (not a total — those are already whole-Rand `valueCents`
 * fields, formatted with `formatMoneyCents` and left untouched). Stuart thinks
 * in Rand-per-tonne, not cents-per-kg (2026-08-26 ruling): a KG product's cost
 * is shown as Rand-per-TONNE (input cents/kg x 1000kg/t / 100c/R = x10); every
 * other base UOM (EACH, L) keeps the existing cents-per-unit -> "R.../unit"
 * convention (PurchaseOrderRegister.tsx's `R${centsToRand(...)} / unit` line
 * price). Input is always the raw per-base-unit cost in ZAR cents — same trio
 * of wire shapes `formatMoneyCents` accepts (bigint, decimal-string, number). */
export function formatCostPerUom(cents: bigint | string | number | null | undefined, baseUom: string): string {
  if (cents === null || cents === undefined) return '—';
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  const negative = n < 0;
  const abs = Math.abs(n);
  if (baseUom === 'KG') {
    const randsPerTonne = abs * 10;
    const formatted = randsPerTonne.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${negative ? '-' : ''}R${formatted} / t`;
  }
  const rands = abs / 100;
  const formatted = rands.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${negative ? '-' : ''}R${formatted} / unit`;
}

/** Quantity/dry-kg decimal-string -> "12,345.678 kg" (3dp, matching the ledger's own
 * quantitySchema precision). */
export function formatKg(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('en-ZA', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg`;
}

/** A dimmed tonnes annotation for a kg value, used alongside `formatKg` on the
 * larger site x period aggregates where a raw kg figure is hard to eyeball. */
export function formatTonnesApprox(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '';
  return `~${(n / 1000).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} t`;
}

/** Plain quantity (not necessarily kg — stock-count-style lines can be EACH/L),
 * trims to 3dp with thousands separators. */
export function formatQty(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-ZA', { maximumFractionDigits: 3 });
}

export function formatPct(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
}

export function formatDate(value: Date | string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatDateTime(value: Date | string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
